/**
 * The single API client.
 *
 * Every request in the application goes through here, which is what makes the correlation header,
 * the error envelope and the token attachment consistent by construction rather than by habit.
 *
 * Where the access token lives depends on which identity provider is configured, because the two
 * have different means of surviving a page reload:
 *
 *   - `AUTH_PROVIDER=entra` (Azure). Memory only. `localStorage` is never used: a token there is
 *     readable by any script injected into the page, and this application handles other
 *     organizations' rate sheets. A reload drops the token and Entra re-issues one silently, so
 *     nothing is lost and nothing is persisted.
 *   - `AUTH_PROVIDER=local` (development). `sessionStorage`, because the local provider has no
 *     silent renewal — without it every reload would mean signing in again. It is scoped to the
 *     tab, cleared when the tab closes, and only ever holds a token this machine minted for a
 *     seeded account.
 *
 * `docs/ARCHITECTURE.md` records this as a deliberate difference between the two providers.
 */
import { IS_LOCAL_AUTH } from '@/auth/identityProvider';
import type { ApiErrorBody, CollectionResponse, ItemResponse, Pagination } from '@/types';

export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
const SESSION_TOKEN_KEY = 'opoc.session.token';

let accessToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

/**
 * Whether a token is one this application signed — HS256 — rather than Microsoft's.
 *
 * Only ours is kept in the tab's session storage. A Microsoft token stays in memory: MSAL keeps
 * its own in session storage and renews it, so a copy here would be a second, staler one. Ours has
 * nothing else to come back from, so on a Microsoft build — dev, whose local sign-in is a side
 * door for named addresses — it was lost on every reload and new tab (2026-10-12).
 */
function signedHere(token: string): boolean {
  try {
    const header = JSON.parse(atob(token.split('.')[0] ?? '')) as { alg?: string };
    return String(header.alg ?? '').toUpperCase().startsWith('HS');
  } catch {
    return false;
  }
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  try {
    if (token && (IS_LOCAL_AUTH || signedHere(token))) sessionStorage.setItem(SESSION_TOKEN_KEY, token);
    else sessionStorage.removeItem(SESSION_TOKEN_KEY);
  } catch {
    // Private browsing or blocked storage: staying in memory is the correct fallback.
  }
}

export function getAccessToken(): string | null {
  if (accessToken) return accessToken;
  try {
    accessToken = sessionStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    accessToken = null;
  }
  return accessToken;
}

/** Where a Microsoft session's token comes from: MSAL, asked each time, renewing when `refresh`. */
export type TokenSource = (options: { refresh: boolean }) => Promise<string | null>;

let tokenSource: TokenSource | null = null;

/**
 * Set while a Microsoft session is open; cleared on a local sign-in or sign-out.
 *
 * The token used to be fetched once on load and never again. Microsoft's last about 78 minutes,
 * so the first call after that was refused and the page bounced to sign-in (2026-10-12). Asking
 * MSAL on every request costs nothing — it answers from its cache, and renews only when the token
 * is close to expiring.
 */
export function setTokenSource(source: TokenSource | null): void {
  tokenSource = source;
}

/** The token to send now: a Microsoft session's from MSAL, otherwise the one held here. */
async function bearer(refresh = false): Promise<string | null> {
  if (tokenSource) {
    try {
      const token = await tokenSource({ refresh });
      if (token) {
        accessToken = token;
        return token;
      }
    } catch {
      // MSAL unreachable for a moment: fall back to what is held, and let the API decide.
    }
  }
  return getAccessToken();
}

async function authHeaders(refresh = false): Promise<Record<string, string>> {
  const token = await bearer(refresh);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

/** A failed request, carrying the backend's stable code so callers can switch on it. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Array<Record<string, unknown>>;
  readonly correlationId: string | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    details: Array<Record<string, unknown>> = [],
    correlationId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.correlationId = correlationId;
  }

  /** Field-level messages, keyed by the field the backend named. */
  get fieldErrors(): Record<string, string> {
    const errors: Record<string, string> = {};
    for (const detail of this.details) {
      const field = typeof detail.field === 'string' ? detail.field : null;
      const message = typeof detail.message === 'string' ? detail.message : this.message;
      if (field) errors[field] = message;
    }
    return errors;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Optimistic concurrency: sent as `If-Match`. */
  etag?: string | null;
  signal?: AbortSignal;
  /** Set for file downloads. */
  raw?: boolean;
  /**
   * A multipart body, for a document upload.
   *
   * Mutually exclusive with `body`. The Content-Type header is deliberately not set: the browser
   * has to write it itself so it can include the multipart boundary, and setting it by hand is
   * the classic way an upload arrives unparseable.
   */
  formData?: FormData;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE_URL}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue;
    params.set(key, String(value));
  }
  const serialised = params.toString();
  return serialised ? `${url}?${serialised}` : url;
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: ApiErrorBody | null = null;
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    body = null;
  }
  const correlationId =
    body?.meta?.correlationId ?? response.headers.get('X-Correlation-ID') ?? undefined;
  return new ApiError(
    response.status,
    body?.error?.code ?? 'REQUEST_FAILED',
    body?.error?.message ?? fallbackMessage(response.status),
    body?.error?.details ?? [],
    correlationId,
  );
}

function fallbackMessage(status: number): string {
  switch (status) {
    case 401:
      return 'Your session has ended. Sign in again.';
    case 403:
      return 'You do not have permission to do that.';
    case 404:
      return 'That record was not found.';
    case 413:
      return 'That file is too large.';
    case 429:
      return 'Too many requests. Wait a minute and try again.';
    default:
      return status >= 500
        ? 'Something went wrong on our side. Try again in a moment.'
        : 'That request could not be completed.';
  }
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', ...(await authHeaders()) };
  if (options.etag) headers['If-Match'] = options.etag;

  let body: BodyInit | undefined;
  if (options.formData !== undefined) {
    body = options.formData;
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  const send = (sent: Record<string, string>): Promise<Response> =>
    fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers: sent,
      body,
      signal: options.signal,
      credentials: 'omit',
    });

  let response = await send(headers);
  // A refused Microsoft token is renewed and the call tried once more before the session ends:
  // a token that expired between MSAL's last answer and this call is a renewal, not a sign-out.
  if (response.status === 401 && tokenSource) {
    const renewed = await authHeaders(true);
    if (renewed.Authorization && renewed.Authorization !== headers.Authorization) {
      response = await send({ ...headers, ...renewed });
    }
  }

  if (response.status === 401) {
    setAccessToken(null);
    onUnauthorized?.();
    throw await toApiError(response);
  }
  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;
  if (options.raw) return (await response.blob()) as T;
  return (await response.json()) as T;
}

/** Unwraps `{ data, meta }`. */
export async function getItem<T>(path: string, options?: RequestOptions): Promise<T> {
  const body = await request<ItemResponse<T>>(path, options);
  return body.data;
}

export interface Paged<T> {
  items: T[];
  pagination: Pagination;
}

/** Unwraps `{ data, pagination, meta }`. */
export async function getCollection<T>(
  path: string,
  options?: RequestOptions,
): Promise<Paged<T>> {
  const body = await request<CollectionResponse<T>>(path, options);
  return { items: body.data, pagination: body.pagination };
}

export async function getList<T>(path: string, options?: RequestOptions): Promise<T[]> {
  return (await getCollection<T>(path, options)).items;
}

/**
 * Downloads a file through the API so the permission check runs again, then hands the browser a
 * blob URL. Raw storage credentials never reach this code.
 */
/**
 * Fetch an authenticated file and open it in a new tab.
 *
 * For a deliverable meant to be *read* rather than saved — the comparison page is HTML. A plain
 * `href` cannot be used for either: the access token is held in memory, deliberately, so a browser
 * navigation carries no Authorization header and the API answers 401.
 *
 * The tab is opened synchronously, before the first `await`, and pointed at the blob afterwards.
 * Opening it after the fetch resolves is what a popup blocker stops.
 */
export async function openFile(path: string, fallbackName: string): Promise<void> {
  const tab = window.open('', '_blank');

  const headers: Record<string, string> = await authHeaders();

  let response: Response;
  try {
    response = await fetch(buildUrl(path), { headers, credentials: 'omit' });
  } catch (error) {
    tab?.close();
    throw error;
  }
  if (!response.ok) {
    tab?.close();
    throw await toApiError(response);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  if (tab) {
    tab.location.href = url;
  } else {
    // The tab was blocked despite being opened in the gesture. Saving it is better than
    // losing it.
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fallbackName;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  }
  // Long enough for the tab to have loaded. Revoking immediately would leave it blank.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * The filename a download names for itself, out of `Content-Disposition`.
 *
 * Two forms travel together (RFC 6266): `filename*=UTF-8''…` carries the real name percent-encoded,
 * and plain `filename="…"` is an ASCII fallback for clients that cannot read the first. So the
 * starred form wins when it is there — otherwise an employer's `naïve.xlsx` would be saved as the
 * fallback's `na_ve.xlsx`, which is the sanitisation leaking into what the user sees.
 *
 * Returns null when the header names nothing usable, so the caller's own fallback applies.
 */
export function filenameFrom(disposition: string): string | null {
  const starred = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(disposition);
  if (starred?.[1]) {
    try {
      return decodeURIComponent(starred[1].trim());
    } catch {
      // A malformed percent-escape is not worth failing a download over; fall through to the
      // plain form, which is always ASCII and always present.
    }
  }
  const plain = /filename\s*=\s*"([^"]*)"/.exec(disposition) ?? /filename\s*=\s*([^;]+)/.exec(disposition);
  const name = plain?.[1]?.trim();
  return name ? name : null;
}

/**
 * A deliverable as a blob, for showing inside the page rather than in a new tab. The same
 * header handling as `downloadFile`: the token is in memory, so a plain `src` cannot carry it.
 */
export async function fetchBlob(path: string): Promise<Blob> {
  const headers: Record<string, string> = await authHeaders();
  const response = await fetch(buildUrl(path), { headers, credentials: 'omit' });
  if (!response.ok) throw await toApiError(response);
  return response.blob();
}

/**
 * A file printed by the browser, which is how a deck becomes a PDF: fetched with the user's token,
 * loaded in a hidden frame, and that frame's print dialog opened, where Save as PDF writes the
 * file. The document's own print styles do the layout. The frame may not run scripts; it is
 * same-origin only so the page can call its print, and may open the print dialog.
 */
export async function printFile(path: string): Promise<void> {
  const blob = await fetchBlob(path);
  const url = URL.createObjectURL(blob);
  const frame = document.createElement('iframe');
  frame.setAttribute('sandbox', 'allow-same-origin allow-modals');
  frame.setAttribute('aria-hidden', 'true');
  Object.assign(frame.style, { position: 'fixed', width: '0', height: '0', border: '0', visibility: 'hidden' });
  let cleaned = false;
  const clean = (): void => {
    if (cleaned) return;
    cleaned = true;
    frame.remove();
    URL.revokeObjectURL(url);
  };
  await new Promise<void>((resolve) => {
    frame.addEventListener('load', () => resolve(), { once: true });
    frame.src = url;
    document.body.append(frame);
  });
  const view = frame.contentWindow;
  if (!view) {
    clean();
    throw new Error('The deck could not be prepared for printing.');
  }
  view.addEventListener('afterprint', clean, { once: true });
  view.focus();
  view.print();
  // A browser that never says the dialog closed still lets the frame go.
  window.setTimeout(clean, 60_000);
}

export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const headers: Record<string, string> = await authHeaders();

  const response = await fetch(buildUrl(path), { headers, credentials: 'omit' });
  if (!response.ok) throw await toApiError(response);

  const name = filenameFrom(response.headers.get('Content-Disposition') ?? '') ?? fallbackName;

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
